// PWA service worker registration utility
const UPDATE_COOLDOWN_KEY = 'pwa_last_update_check';
const UPDATE_COOLDOWN_PERIOD = 6 * 60 * 60 * 1000; // 6 hours
const UPDATE_PROMPT_COOLDOWN_KEY = 'pwa_last_update_prompt';
const UPDATE_PROMPT_COOLDOWN_PERIOD = 30 * 60 * 1000; // 30 minutes

function canShowUpdatePrompt(): boolean {
  const lastPrompt = localStorage.getItem(UPDATE_PROMPT_COOLDOWN_KEY);
  if (!lastPrompt) return true;
  
  const timeSinceLastPrompt = Date.now() - parseInt(lastPrompt, 10);
  return timeSinceLastPrompt > UPDATE_PROMPT_COOLDOWN_PERIOD;
}

function markUpdatePromptShown(): void {
  localStorage.setItem(UPDATE_PROMPT_COOLDOWN_KEY, Date.now().toString());
}

function canCheckForUpdates(): boolean {
  const lastCheck = localStorage.getItem(UPDATE_COOLDOWN_KEY);
  if (!lastCheck) return true;
  
  const timeSinceLastCheck = Date.now() - parseInt(lastCheck, 10);
  return timeSinceLastCheck > UPDATE_COOLDOWN_PERIOD;
}

function markUpdateCheckPerformed(): void {
  localStorage.setItem(UPDATE_COOLDOWN_KEY, Date.now().toString());
}

export function registerServiceWorker() {
  if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').then(
        (registration) => {
          console.log('Service Worker registered with scope:', registration.scope);
          
          // Only check for updates if cooldown period has passed
          const checkForUpdates = () => {
            if (canCheckForUpdates()) {
              console.log('Checking for service worker updates...');
              registration.update();
              markUpdateCheckPerformed();
            } else {
              console.log('Skipping update check due to cooldown period');
            }
          };
          
          // Initial check after a delay to avoid immediate update checks
          setTimeout(checkForUpdates, 10000); // Wait 10 seconds after load
          
          // Check for updates less frequently - every 4 hours instead of 1
          setInterval(checkForUpdates, 4 * 60 * 60 * 1000);
          
          registration.addEventListener('updatefound', () => {
            console.log('Service worker update found');
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // New content is available, but only show prompt if cooldown allows
                  if (canShowUpdatePrompt()) {
                    console.log('Showing update prompt to user');
                    const shouldUpdate = window.confirm(
                      'New content available. Click OK to refresh and get the latest updates.'
                    );
                    
                    if (shouldUpdate) {
                      markUpdatePromptShown();
                      window.location.reload();
                    } else {
                      // Mark as shown even if user declined to prevent immediate re-prompting
                      markUpdatePromptShown();
                    }
                  } else {
                    console.log('Skipping update prompt due to cooldown period');
                  }
                }
              });
            }
          });
        },
        (error) => {
          console.error('Service Worker registration failed:', error);
        }
      );
    });
  }
}

// Alternative implementation using vite-plugin-pwa if available
export function registerServiceWorkerWithPWA() {
  // @ts-ignore - This will be available at runtime if vite-plugin-pwa is configured
  if (typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'production') {
    import('virtual:pwa-register').then(({ registerSW }) => {
      const updateSW = registerSW({
        onNeedRefresh() {
          // Only show prompt if cooldown allows
          if (canShowUpdatePrompt()) {
            console.log('Showing PWA update prompt to user');
            const shouldUpdate = window.confirm(
              'New content available. Click OK to refresh and get the latest updates.'
            );
            
            if (shouldUpdate) {
              markUpdatePromptShown();
              updateSW(true);
            } else {
              // Mark as shown even if user declined to prevent immediate re-prompting
              markUpdatePromptShown();
            }
          } else {
            console.log('Skipping PWA update prompt due to cooldown period');
          }
        },
        onOfflineReady() {
          console.log('App ready to work offline');
        },
        onRegisteredSW(swUrl, r) {
          console.log('Service Worker registered:', swUrl);
          
          // Check for updates with proper cooldown logic
          const checkForUpdates = () => {
            if (canCheckForUpdates()) {
              console.log('Checking for PWA service worker updates...');
              r?.update();
              markUpdateCheckPerformed();
            } else {
              console.log('Skipping PWA update check due to cooldown period');
            }
          };
          
          // Initial check after a delay
          setTimeout(checkForUpdates, 10000); // Wait 10 seconds after registration
          
          // Check for updates less frequently - every 4 hours
          if (r) {
            setInterval(checkForUpdates, 4 * 60 * 60 * 1000);
          }
        },
        onRegisterError(error) {
          console.error('Service Worker registration error:', error);
        }
      });

      return updateSW;
    }).catch(err => {
      console.log('Virtual PWA register not available, using fallback');
      registerServiceWorker();
    });
  }
}